import React from "react";
import CustomSelect from "@components/common/CustomSelect";
import { XCircle } from "lucide-react";
import { CustomSelectItemProps } from "@components/common/CommonTypes";
import { capitalizeWords } from "@utils/utils";
import { UsecaseContext } from "@services/providers/UsecaseProvider";

interface AnomalyDefeatItemProps {
  className?: string;
  data: any;
  type: string;
}

const AnomalyDefeatItem: React.FC<AnomalyDefeatItemProps> = ({
  className = "",
  data,
  type = "includeMeta",
}) => {
  const { DefeatData, updateDefeatData, usecaseData, metaMapping, FieldData } =
    React.useContext(UsecaseContext);

  const user_or_entity = usecaseData.event_log_mapping.event_type;
  const categoryDropdown: CustomSelectItemProps[] = [];
  if (user_or_entity in metaMapping) {
    metaMapping[user_or_entity]?.forEach((cat: any) => {
      if (cat.usecase && !cat.hide_meta) {
        categoryDropdown.push({
          label: cat.display_name || capitalizeWords(cat.name),
          value: cat.name,
        });
      }
    });
  }

  // filter categoryDropdown to remove already present categories in DefeatData[type], except for the current data.condition
  const filteredCategoryDropdown = categoryDropdown.filter((cat) => {
    const alreadyPresent = (DefeatData?.includeMeta || []).some(
      (itm: any) => itm?.condition === cat?.value,
    );
    if (cat.value === data.condition) {
      return true; // always include the current condition
    }
    return !alreadyPresent;
  });

  function changeCategory(e: any) {
    if (user_or_entity in metaMapping === false) return;
    const selectedCategory = metaMapping[user_or_entity]?.find(
      (m: any) => m.name == e,
    );
    const mappings = selectedCategory ? selectedCategory.mapping : {};

    const match = DefeatData[type].find(
      (item: any) => item.metaId == data.metaId,
    );
    if (match) {
      match["condition"] = e;
      match["dataType"] = selectedCategory
        ? selectedCategory.valueType.toLowerCase()
        : "";
      match["isCustom"] = selectedCategory
        ? selectedCategory.isCustom
          ? true
          : false
        : false;
      match["type"] =
        Object.keys(mappings || {}).length > 0 ? "fromList" : "logField";
      match["valueType"] = selectedCategory ? selectedCategory.valueType : "";
      match["value"] = "";
      match["operation"] = "";
      updateDefeatData({ ...DefeatData });
    }
  }

  function changeDataType(e: string) {
    const match = DefeatData[type].find(
      (item: any) => item.metaId == data.metaId,
    );
    if (match) {
      match["operation"] = e;
      if (["increment", "decrement"].includes(e.toLowerCase())) {
        match["value"] = "1";
      }
      updateDefeatData({ ...DefeatData });
    }
  }

  function changeValue(e: any) {
    const match = DefeatData[type].find(
      (item: any) => item.metaId == data.metaId,
    );
    if (match) {
      match["value"] = e;
      updateDefeatData({ ...DefeatData });
    }
  }

  function removeItem() {
    updateDefeatData({
      ...DefeatData,
      [type]: DefeatData[type].filter(
        (item: any) => item.metaId !== data.metaId,
      ),
    });
  }

  function changeMetaLogSource(source: string, catType?: string) {
    if ("data" in FieldData) {
      const standards = FieldData.data["standard"];
      const tempFields: any = [];
      const logs = FieldData.data["logs"];
      const ls = source;
      if (ls in logs) {
        const fields =
          logs[ls]?.data?.length > 0 ? logs[ls].data : logs[ls].fields;
        for (const field of fields) {
          if (field.usecaseField === 1 || field.for_rule_builder === 1) {
            if (
              FieldData.data.seen_by_log[ls] &&
              FieldData.data.seen_by_log[ls][field.field]
            ) {
              standards[FieldData.data.seen_by_log[ls][field.field]]["name"] =
                field.field;
              tempFields.push(
                standards[FieldData.data.seen_by_log[ls][field.field]],
              );
            } else if (FieldData.data.seen[field.field] in standards) {
              standards[FieldData.data.seen[field.field]]["name"] = field.field;
              tempFields.push(standards[FieldData.data.seen[field.field]]);
            } else if (field.field in standards) {
              standards[field.field]["name"] = field.field;
              tempFields.push(standards[field.field]);
            }
          }
        }
      }
      const normalizedCatType =
        catType?.toLowerCase() === "int" ||
        catType?.toLowerCase() === "integer" ||
        catType?.toLowerCase() === "float"
          ? "number"
          : catType?.toLowerCase();

      const itm = tempFields
        .filter((t: any) => {
          const fieldType = t.type?.toLowerCase();

          const normalizedFieldType =
            fieldType === "int" ||
            fieldType === "integer" ||
            fieldType === "float"
              ? "number"
              : fieldType;

          return (
            normalizedFieldType?.includes(normalizedCatType || "") ||
            normalizedCatType === "" ||
            normalizedCatType === "array"
          );
        })
        .map((tmp: any) => ({
          label: tmp.display_name || tmp.name,
          value: tmp.name,
        }));

      return itm;
    }
  }

  let log_source = (data.log_source || {}).value || "";
  if (log_source == "") {
    log_source = usecaseData.block_list[0]?.log_source?.value;
  }
  if (log_source == "") {
    log_source = usecaseData.block_list[0]?.group_list[0]?.log_source?.value;
  }

  const selectedCategory = metaMapping[user_or_entity]?.find(
    (m: any) => m.name == data.condition,
  );
  const actions = (
    selectedCategory && selectedCategory.operations
      ? selectedCategory.operations
      : []
  ).map((op: string) => ({
    label: op
      .split("_")
      .map((word) => capitalizeWords(word))
      .join(" "),
    value: op,
  }));
  let mappings: CustomSelectItemProps[] = [];
  if (selectedCategory) {
    if (Object.keys(selectedCategory.mapping || {}).length > 0) {
      Object.keys(selectedCategory.mapping || {}).forEach((key: string) => {
        mappings.push({
          label: key.toString(),
          value: key.toString(), // change requested by Yadhu in linkshadow/linkshadow_updates#3612
          // value: selectedCategory.mapping[key].toString(),
        });
      });
    } else {
      mappings = changeMetaLogSource(
        log_source,
        selectedCategory.valueType.toLowerCase(),
      );
    }
  }

  return (
    <>
      <div
        className={
          "w-full flex gap-2 border-b border-zinc-800 pb-2 " + className
        }
      >
        <div className="w-[20%]">
          <CustomSelect
            data={filteredCategoryDropdown}
            defaultValue={data.condition}
            placeholder="Category..."
            onSelect={(e: any) => changeCategory(e)}
          />
        </div>
        <div className="w-[20%]">
          <CustomSelect
            data={actions}
            defaultValue={
              data.valueType === "bool"
                ? String(data.boolValue) // "true" / "false"
                : data.operation
            }
            placeholder="Action..."
            onSelect={(e: any) => changeDataType(e)}
          />
        </div>
        <div className="flex-1 flex items-center text-sm">
          {["increment", "decrement"].includes(data.operation) ? (
            <span>of 1</span>
          ) : (
            <div className="w-80">
              <CustomSelect
                data={mappings}
                placeholder="Values..."
                defaultValue={data.value}
                onSelect={(e: any) => changeValue(e)}
              />
            </div>
          )}
        </div>
        <div className="justify-center flex items-center">
          <XCircle
            size={20}
            className="cursor-pointer ms-2 text-primary"
            onClick={() => removeItem()}
          />
        </div>
      </div>
    </>
  );
};

export default AnomalyDefeatItem;
